import { useNavigate } from '@tanstack/react-router';
import { Globe, History, Server } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatPolicyTime } from '@shared/ai-data-policy/labels';
import { bidiIsolate } from '@shared/ai-data-policy/names';

import { goToDataPolicySettings } from '../data-policy/navigation';
import type { ChatSystemNote } from '../types';

export function SystemNote({ note }: { note: ChatSystemNote }) {
  const { t, i18n } = useTranslation('ai-cio');
  const navigate = useNavigate();
  const name = note.actorName
    ? bidiIsolate(note.actorName, i18n.dir() === 'rtl')
    : t('systemNote.someone', 'An admin');
  const time = formatPolicyTime(note.at);
  const model = note.modelLabel ?? t('picker.model', 'Model');
  const changedTitle = time
    ? t('systemNote.changedTitle', 'Data policy changed · {{time}}', { time })
    : t('systemNote.changedTitlePlain', 'Data policy changed');

  let Icon = Server;
  let title: string;
  let body: string;
  switch (note.kind) {
    case 'sn3':
      Icon = Globe;
      title = t('systemNote.sn3Title', 'Investor data can go to your selected model');
      body = t(
        'systemNote.sn3Body',
        'Investor and CRM data goes to the model you select. To keep it inside Investbanq, select the Investbanq model.',
      );
      break;
    case 'sn4':
      Icon = History;
      title = changedTitle;
      body = t(
        'systemNote.sn4Body',
        '{{name}} allowed commercial models to process investor and CRM data. Applies to new requests.',
        { name },
      );
      break;
    case 'sn5':
      Icon = History;
      title = changedTitle;
      body = t(
        'systemNote.sn5Body',
        '{{name}} stopped commercial models from processing investor and CRM data. Applies to new requests.',
        { name },
      );
      break;
    case 'sn6':
      Icon = History;
      title = time
        ? t('systemNote.setTitle', 'Data policy set · {{time}}', { time })
        : t('systemNote.setTitlePlain', 'Data policy set');
      body = t(
        'systemNote.sn4Body',
        '{{name}} allowed commercial models to process investor and CRM data. Applies to new requests.',
        { name },
      );
      break;
    case 'sn2':
      title = t('systemNote.sn1Title', 'Investor data stays on the Investbanq model');
      body = t(
        'systemNote.sn2Body',
        'No data policy yet, so only the Investbanq model processes investor and CRM data. Other questions use {{model}}.',
        { model },
      );
      break;
    default:
      title = t('systemNote.sn1Title', 'Investor data stays on the Investbanq model');
      body = t(
        'systemNote.sn1Body',
        'Your workspace processes investor and CRM data only with the Investbanq model. Other questions still use {{model}}.',
        { model },
      );
  }

  return (
    <div
      role="status"
      className="flex flex-col items-center gap-1 py-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-150"
    >
      <div className="flex w-full items-center gap-2">
        <span className="h-px flex-1 bg-border" aria-hidden />
        <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="text-xs font-medium text-muted-foreground">{title}</span>
        <span className="h-px flex-1 bg-border" aria-hidden />
      </div>
      <p className="max-w-[400px] text-center text-xs text-muted-foreground">{body}</p>
      <button
        type="button"
        onClick={() => {
          goToDataPolicySettings(navigate);
        }}
        className="text-xs font-medium text-foreground underline underline-offset-2"
      >
        {t('systemNote.viewPolicy', 'View data policy')}
      </button>
    </div>
  );
}
